import { useEffect, useRef, useState } from 'react';
import { Button, Field, Initials, Notice, Spinner } from '../components/ui';
import { Dialog } from '../components/Dialog';
import { OrgCodePanel } from '../components/OrgCodePanel';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { relativeDate, useSchool } from '../lib/school';
import {
  parseEmails,
  useInvite,
  useInviteAction,
  useRequestAction,
  useRemovedAction,
  useMemberAction,
  useNudge,
  usePeople,
  type InviteResult,
  type Person,
} from '../lib/people';
import { useMe } from '../lib/session';
import './Page.css';
import './People.css';
import { Choice } from './Welcome';

const ROLE_LABEL: Record<string, string> = { teacher: 'Teacher', school_leader: 'Leader', system_admin: 'Admin' };

type Confirm = { title: string; body: string; action: string; danger?: boolean; run: () => Promise<unknown> } | null;

export function PeoplePage() {
  const me = useMe();
  const people = usePeople();
  const school = useSchool();
  const memberAction = useMemberAction();
  const inviteAction = useInviteAction();
  const requestAction = useRequestAction();
  const removedAction = useRemovedAction();
  // Overview's "Invite people" lands here with ?invite=1 to open the dialog.
  const [params, setParams] = useSearchParams();
  const [inviteOpen, setInviteOpen] = useState(params.get('invite') === '1');
  useEffect(() => {
    if (params.get('invite') === '1') setParams({}, { replace: true });
  }, [params, setParams]);
  const [confirm, setConfirm] = useState<Confirm>(null);
  /** The nudge dialog: everyone (null person) or one person. */
  const [nudging, setNudging] = useState<{ person: Person | null } | null>(null);
  const [flash, setFlash] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const run = async (fn: () => Promise<unknown>, success: string) => {
    try {
      await fn();
      setFlash({ tone: 'success', text: success });
    } catch (err) {
      setFlash({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    }
  };

  if (people.isLoading) return <Spinner />;
  if (people.isError || !people.data) {
    return <Notice tone="error">We couldn&rsquo;t load your team. Refresh the page to try again.</Notice>;
  }
  const { seats, members, invites } = people.data;
  const requests = people.data.requests ?? [];
  const removed = people.data.removed ?? [];
  const isOwner = people.data.me.isOwner;

  /** What this viewer may do to this person. Owners manage leaders; leaders manage teachers. */
  const actionsFor = (p: Person) => {
    const acts: { label: string; danger?: boolean; onSelect: () => void }[] = [];
    if (p.role === 'system_admin') return acts;
    const self = p.id === me.id;
    if (isOwner && !self) {
      if (p.role === 'teacher') {
        acts.push({
          label: 'Make a leader',
          onSelect: () =>
            setConfirm({
              title: `Make ${p.name} a leader?`,
              body: 'Leaders see the Leadership Hub and can invite and remove teachers.',
              action: 'Make leader',
              run: () => run(() => memberAction.mutateAsync({ id: p.id, change: { role: 'school_leader' } }), `${p.name} is now a leader.`),
            }),
        });
      } else {
        acts.push({
          label: 'Make a teacher',
          onSelect: () =>
            setConfirm({
              title: `Make ${p.name} a teacher?`,
              body: 'They will lose access to the Leadership Hub and people management.',
              action: 'Make teacher',
              run: () => run(() => memberAction.mutateAsync({ id: p.id, change: { role: 'teacher' } }), `${p.name} is now a teacher.`),
            }),
        });
        acts.push(
          p.isOwner
            ? {
                label: 'Remove as owner',
                onSelect: () =>
                  setConfirm({
                    title: `Remove ${p.name} as an owner?`,
                    body: 'They stay a leader but can no longer manage seats or other leaders.',
                    action: 'Remove as owner',
                    run: () => run(() => memberAction.mutateAsync({ id: p.id, change: { isOwner: false } }), `${p.name} is no longer an owner.`),
                  }),
              }
            : {
                label: 'Make an owner',
                onSelect: () =>
                  setConfirm({
                    title: `Make ${p.name} an account owner?`,
                    body: 'Owners manage seats, invite leaders and can change or remove other leaders.',
                    action: 'Make owner',
                    run: () => run(() => memberAction.mutateAsync({ id: p.id, change: { isOwner: true } }), `${p.name} is now an owner.`),
                  }),
              },
        );
      }
    }
    const ownerCount = members.filter((m) => m.isOwner).length;
    if (self && p.isOwner && ownerCount > 1) {
      acts.push({
        label: 'Step down as owner',
        onSelect: () =>
          setConfirm({
            title: 'Step down as an owner?',
            body: 'You stay a leader. Another owner will need to manage seats and leaders.',
            action: 'Step down',
            run: () => run(() => memberAction.mutateAsync({ id: p.id, change: { isOwner: false } }), 'You’re no longer an owner.'),
          }),
      });
    }
    if (!self) acts.push({ label: 'Send a nudge', onSelect: () => setNudging({ person: p }) });
    if (!self && (p.role === 'teacher' || isOwner)) {
      acts.push({
        label: 'Remove from organisation',
        danger: true,
        onSelect: () =>
          setConfirm({
            title: `Remove ${p.name} from ${school.data?.name ?? 'your school'}?`,
            body: 'Their seat is freed and their EducAitor account is paused. Their lessons, past ones too, no longer count towards your organisation. They keep their account and lessons, and we can move them to an individual account if they ask.',
            action: 'Remove',
            danger: true,
            run: () => run(() => memberAction.mutateAsync({ id: p.id, change: 'remove' }), `${p.name} has been removed.`),
          }),
      });
    }
    return acts;
  };

  const leaders = members.filter((m) => m.role !== 'teacher');
  const teachers = members.filter((m) => m.role === 'teacher');

  return (
    <div className="page">
      <header className="people-head">
        <div className="page-head">
          <h1>People</h1>
          <p className="muted">Invite staff, manage seats and decide who leads. Lesson counts show use of EducAitor, not performance.</p>
        </div>
        <div className="people-head-actions">
          <Button variant="secondary" onClick={() => setNudging({ person: null })}>
            Nudge staff
          </Button>
          <Button onClick={() => setInviteOpen(true)} disabled={seats.available === 0}>
            Invite people
          </Button>
        </div>
      </header>

      <div aria-live="polite">{flash ? <Notice tone={flash.tone}>{flash.text}</Notice> : null}</div>

      <SeatBar seats={seats} people={members.length} admins={members.filter((m) => m.role === 'system_admin').length} />

      {requests.length > 0 ? (
        <section className="people-section">
          <h2>Waiting for approval</h2>
          <p className="muted">These people joined with your organisation code. They can&rsquo;t use EducAitor until a leader approves them.</p>
          <div className="panel">
            <div className="list">
              {requests.map((r) => (
                <div className="list-row people-invite" key={r.id}>
                  <Initials name={r.name} />
                  <div className="list-main">
                    <p className="list-title">{r.name}</p>
                    <p className="list-sub">
                      {r.email} &middot; asked {relativeDate(r.requestedAt).toLowerCase()}
                    </p>
                  </div>
                  <div className="row-actions">
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setConfirm({
                          title: `Decline ${r.name}?`,
                          body: 'They won’t be able to use EducAitor with your school. They can still be invited by email later.',
                          action: 'Decline',
                          danger: true,
                          run: () => run(() => requestAction.mutateAsync({ id: r.id, action: 'decline' }), `${r.name} was declined.`),
                        })
                      }
                    >
                      Decline
                    </Button>
                    <Button onClick={() => run(() => requestAction.mutateAsync({ id: r.id, action: 'approve' }), `${r.name} can now use EducAitor.`)}>
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <PeopleSection title="Leaders" people={leaders} actionsFor={actionsFor} meId={me.id} />
      <PeopleSection
        title="Teachers"
        people={teachers}
        actionsFor={actionsFor}
        meId={me.id}
        empty="No teachers yet. Invite them by email, or share your organisation code."
      />

      {invites.length > 0 ? (
        <section className="people-section">
          <h2>Invited</h2>
          <div className="panel">
            <div className="list">
              {invites.map((i) => (
                <div className="list-row people-invite" key={i.id}>
                  <span className="invite-dot" aria-hidden="true" />
                  <div className="list-main">
                    <p className="list-title">{i.email}</p>
                    <p className="list-sub">
                      {i.expired ? 'Invite expired' : `Invited ${relativeDate(i.createdAt).toLowerCase()}`}
                      {i.invitedBy ? ` by ${i.invitedBy}` : ''}
                    </p>
                  </div>
                  <span className={`chip${i.role === 'school_leader' ? ' chip-accent' : ''}`}>{ROLE_LABEL[i.role]}</span>
                  {/* Leader invites are sent, resent and cancelled by owners only. */}
                  {i.role === 'teacher' || isOwner ? (
                    <div className="row-actions">
                      <Button
                        variant="ghost"
                        onClick={() => run(() => inviteAction.mutateAsync({ id: i.id, action: 'resend' }), `Invite sent again to ${i.email}.`)}
                      >
                        Resend
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() =>
                          setConfirm({
                            title: 'Cancel this invite?',
                            body: `${i.email} won’t be able to use it, and the seat is freed.`,
                            action: 'Cancel invite',
                            danger: true,
                            run: () => run(() => inviteAction.mutateAsync({ id: i.id, action: 'revoke' }), 'Invite cancelled.'),
                          })
                        }
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {removed.length > 0 ? (
        <section className="people-section">
          <h2>Removed</h2>
          <p className="muted">
            Their accounts are paused and their lessons don&rsquo;t count towards your organisation. Adding someone back gives them a seat as a teacher.
          </p>
          <div className="panel">
            <div className="list">
              {removed.map((r) => (
                <div className="list-row people-invite" key={r.id}>
                  <Initials name={r.name} />
                  <div className="list-main">
                    <p className="list-title">{r.name}</p>
                    <p className="list-sub">
                      {r.email}
                      {r.removedAt ? <> &middot; removed {relativeDate(r.removedAt).toLowerCase()}</> : null}
                    </p>
                  </div>
                  <div className="row-actions">
                    <Button
                      variant="ghost"
                      onClick={() =>
                        run(
                          () => removedAction.mutateAsync({ id: r.id, action: 'clear' }),
                          `${r.name} is off this list. Their account stays paused unless they join again.`,
                        )
                      }
                    >
                      Clear
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={seats.available === 0}
                      title={seats.available === 0 ? 'All your seats are in use' : undefined}
                      onClick={() =>
                        setConfirm({
                          title: `Add ${r.name} back?`,
                          body: 'They take a seat as a teacher, can use EducAitor again, and their lessons count towards your organisation again.',
                          action: 'Add back',
                          run: () => run(() => removedAction.mutateAsync({ id: r.id, action: 'restore' }), `${r.name} is back in.`),
                        })
                      }
                    >
                      Add back
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <OrgCodePanel />

      <NudgeDialog
        open={nudging != null}
        person={nudging?.person ?? null}
        members={members.filter((m) => m.id !== me.id && m.role !== 'system_admin')}
        myName={me.name}
        schoolName={school.data?.name ?? 'your school'}
        onClose={() => setNudging(null)}
        onDone={(text) => {
          setNudging(null);
          setFlash({ tone: 'success', text });
        }}
      />

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        canInviteLeaders={isOwner}
        schoolCode={school.data?.invite_code ?? null}
        available={seats.available}
        memberEmails={members.map((m) => m.email.toLowerCase())}
        invitedEmails={invites.filter((i) => !i.expired).map((i) => i.email.toLowerCase())}
      />

      <Dialog open={confirm != null} onClose={() => setConfirm(null)} title={confirm?.title ?? ''}>
        <p className="muted">{confirm?.body}</p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setConfirm(null)}>
            Keep as is
          </Button>
          <Button
            variant={confirm?.danger ? 'danger' : 'primary'}
            loading={memberAction.isPending || inviteAction.isPending || removedAction.isPending}
            onClick={async () => {
              await confirm?.run();
              setConfirm(null);
            }}
          >
            {confirm?.action}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * With a seat limit: seats used against it (EducAitor admins never use a
 * seat). Without one: simply how many people are in the school.
 */
function SeatBar({
  seats,
  people,
  admins,
}: {
  seats: { limit: number | null; members: number; pendingInvites: number; used: number; available: number | null };
  people: number;
  admins: number;
}) {
  const share = seats.limit ? Math.min(1, seats.used / seats.limit) : 0;
  // More people than seats (say, seats were set below the team's size): nobody
  // loses access, but inviting or approving anyone waits until there's room.
  const over = seats.limit != null && seats.used > seats.limit ? seats.used - seats.limit : 0;
  return (
    <section className="panel seat-panel">
      <div className="seat-top">
        <p>
          <strong className="tabular">{seats.limit != null ? `${seats.used} of ${seats.limit}` : people}</strong>{' '}
          {seats.limit != null ? 'seats used' : people === 1 ? 'person' : 'people'}
          {seats.pendingInvites ? <span className="muted"> · {seats.pendingInvites} invited</span> : null}
        </p>
        {seats.limit == null ? null : (
          <p className={over ? 'seat-over' : 'muted'}>
            {over
              ? `${over} over your ${seats.limit} seats`
              : admins > 0 && seats.available !== 0
                ? `${seats.available} left \u00b7 EducAitor admins don\u2019t use a seat`
                : seats.available === 0
                  ? 'All seats in use.'
                  : `${seats.available} left`}
          </p>
        )}
      </div>
      {seats.limit != null ? (
        <div className="seat-track" role="progressbar" aria-valuemin={0} aria-valuemax={seats.limit} aria-valuenow={seats.used} aria-label="Seats used">
          <div className="seat-fill" style={{ width: `${share * 100}%` }} data-over={over > 0 || undefined} />
        </div>
      ) : null}
      {over ? (
        <p className="seat-over-note">
          Everyone keeps their access. To invite or approve anyone new, remove {over === 1 ? 'someone' : `${over} people`} or ask EducAitor for more seats.
        </p>
      ) : null}
    </section>
  );
}

function PeopleSection({
  title,
  people,
  actionsFor,
  meId,
  empty,
}: {
  title: string;
  people: Person[];
  actionsFor: (p: Person) => { label: string; danger?: boolean; onSelect: () => void }[];
  meId: string;
  empty?: string;
}) {
  return (
    <section className="people-section">
      <h2>
        {title} <span className="faint tabular">{people.length}</span>
      </h2>
      <div className="panel">
        {people.length === 0 ? (
          <p className="muted">{empty}</p>
        ) : (
          <div className="list">
            {people.map((p) => (
              <div className="list-row" key={p.id}>
                <Initials name={p.name} />
                <div className="list-main">
                  <p className="list-title">
                    {p.name}
                    {p.id === meId ? <span className="faint"> (you)</span> : null}
                  </p>
                  <p className="list-sub">{p.email}</p>
                </div>
                <span className="row-chips">
                  {p.isOwner ? <span className="chip chip-accent">Owner</span> : null}
                  {p.role === 'system_admin' ? <span className="chip">Admin</span> : null}
                </span>
                <span className="meta">
                  {p.lessons === 0
                    ? 'No lessons yet'
                    : `${p.lessons} ${p.lessons === 1 ? 'lesson' : 'lessons'} \u00b7 active ${relativeDate(p.lastActiveAt).toLowerCase()}`}
                </span>
                <RowMenu actions={actionsFor(p)} label={`Actions for ${p.name}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function RowMenu({ actions, label }: { actions: { label: string; danger?: boolean; onSelect: () => void }[]; label: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  if (actions.length === 0) return <span className="menu-spacer" />;
  return (
    <div className="menu" ref={ref}>
      <button className="menu-button" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">&middot;&middot;&middot;</span>
      </button>
      {open ? (
        <div className="menu-list" role="menu">
          {actions.map((a) => (
            <button
              key={a.label}
              role="menuitem"
              className={a.danger ? 'menu-danger' : undefined}
              onClick={() => {
                setOpen(false);
                a.onSelect();
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function InviteDialog({
  open,
  onClose,
  canInviteLeaders,
  schoolCode,
  available,
  memberEmails,
  invitedEmails,
}: {
  open: boolean;
  onClose: () => void;
  canInviteLeaders: boolean;
  schoolCode: string | null;
  available: number | null;
  memberEmails: string[];
  invitedEmails: string[];
}) {
  const invite = useInvite();
  const [text, setText] = useState('');
  const [role, setRole] = useState<'teacher' | 'school_leader'>('teacher');
  const [result, setResult] = useState<InviteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Every pasted entry becomes a chip saying what will happen to it (P-01).
  const entries = classifyEmails(text, memberEmails, invitedEmails);
  const emails = entries.filter((e) => e.status === 'valid').map((e) => e.email);
  const addFile = async (file: File | undefined) => {
    if (!file) return;
    const found = parseEmails(await file.text());
    setText((t) => [t.trim(), ...found].filter(Boolean).join('\n'));
  };

  useEffect(() => {
    if (open) {
      setText('');
      setRole('teacher');
      setResult(null);
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setError(null);
    try {
      setResult(await invite.mutateAsync({ emails, role }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={result ? 'Invites sent' : 'Invite people'}>
      {result ? (
        <>
          <p>
            {result.invited.length} {result.invited.length === 1 ? 'person' : 'people'} invited.
          </p>
          {result.skipped.length ? (
            <ul className="skipped">
              {result.skipped.map((s) => (
                <li key={s.email}>
                  <strong>{s.email}</strong> <span className="muted">{s.reason.toLowerCase()}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {result.invited.length > 0 && result.emailed < result.invited.length ? (
            <Notice>
              Invite emails aren&rsquo;t switched on yet, so let them know yourself: download the EducAitor app and join with the organisation code
              {schoolCode ? <strong> {schoolCode}</strong> : null}. Their seat is saved for 14 days.
            </Notice>
          ) : null}
          <div className="dialog-actions">
            <Button onClick={onClose}>Done</Button>
          </div>
        </>
      ) : (
        <>
          <p className="muted">
            Paste email addresses, one per line or separated by commas. Each person gets an email with a button that opens the app and joins your organisation,
            with no code needed.
          </p>
          <label htmlFor="invite-emails" className="sr-label">
            Email addresses
          </label>
          <textarea
            id="invite-emails"
            className="textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'jane.smith@school.org\nsam.patel@school.org'}
            autoFocus
          />
          <label className="link-button invite-file">
            Add from a CSV file
            <input type="file" accept=".csv,.txt,text/csv,text/plain" className="sr-label" onChange={(e) => addFile(e.target.files?.[0])} />
          </label>
          {entries.length ? (
            <ul className="invite-chips" aria-label="Addresses">
              {entries.map((e) => (
                <li key={e.email} className={`invite-chip invite-chip-${e.status}`}>
                  {e.email}
                  {e.status !== 'valid' ? <span className="invite-chip-note"> · {STATUS_NOTE[e.status]}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="invite-meta">
            {canInviteLeaders ? (
              <div className="segmented" role="group" aria-label="Invite as">
                <button type="button" aria-pressed={role === 'teacher'} onClick={() => setRole('teacher')}>
                  Teachers
                </button>
                <button type="button" aria-pressed={role === 'school_leader'} onClick={() => setRole('school_leader')}>
                  Leaders
                </button>
              </div>
            ) : (
              <span />
            )}
            <span className="muted tabular">
              {emails.length} to invite
              {entries.length > emails.length ? ` · ${entries.length - emails.length} won’t be sent` : ''}
              {available != null ? ` · ${available} seat${available === 1 ? '' : 's'} left` : ''}
            </span>
          </div>
          {error ? <Notice tone="error">{error}</Notice> : null}
          <div className="dialog-actions">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} loading={invite.isPending} disabled={emails.length === 0}>
              Send {emails.length > 0 ? emails.length : ''} {emails.length === 1 ? 'invite' : 'invites'}
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
}

type EntryStatus = 'valid' | 'invalid' | 'member' | 'invited';
const STATUS_NOTE: Record<Exclude<EntryStatus, 'valid'>, string> = {
  invalid: 'not an email address',
  member: 'already a member',
  invited: 'already invited',
};

/** Splits pasted text into unique entries and says what will happen to each. */
function classifyEmails(text: string, members: string[], invited: string[]): { email: string; status: EntryStatus }[] {
  const seen = new Set<string>();
  const out: { email: string; status: EntryStatus }[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const email = raw
      .replace(/^[<"'(]+|[>"')]+$/g, '')
      .trim()
      .toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    const status: EntryStatus = !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email)
      ? 'invalid'
      : members.includes(email)
        ? 'member'
        : invited.includes(email)
          ? 'invited'
          : 'valid';
    out.push({ email, status });
  }
  return out;
}

const QUIET_DAYS = 14;
const NUDGE_DAYS = 7;
const daysSince = (iso: string | null | undefined) => (iso ? (Date.now() - new Date(iso).getTime()) / 86_400_000 : Infinity);

/**
 * Ask staff to record a lesson: a push notification with your name and an
 * optional short note. Everyone quiet for two weeks, everyone, or one
 * person. Nobody gets more than one nudge a week, whoever sends it.
 */
function NudgeDialog({
  open,
  person,
  members,
  myName,
  schoolName,
  onClose,
  onDone,
}: {
  open: boolean;
  person: Person | null;
  members: Person[];
  myName: string;
  schoolName: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const nudge = useNudge();
  const [who, setWho] = useState<'quiet' | 'all'>('quiet');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setWho('quiet');
    setNote('');
    setError(null);
  }, [open]);

  const quiet = members.filter((m) => daysSince(m.lastActiveAt) > QUIET_DAYS);
  const targets = person ? [person] : who === 'quiet' ? quiet : members;
  const recent = targets.filter((m) => daysSince(m.lastNudgedAt) < NUDGE_DAYS);
  const first = myName.split(' ')[0] || myName;
  const body = note.trim() ? `${first}: \u201c${note.trim()}\u201d` : `${first} at ${schoolName} would love you to record a lesson this week.`;

  const send = async () => {
    setError(null);
    try {
      const r = await nudge.mutateAsync({ who: person ? [person.id] : who === 'quiet' ? 'quiet' : members.map((m) => m.id), note: note.trim() || undefined });
      const skipped = r.skipped.length
        ? ` ${r.skipped.length === 1 ? `${r.skipped[0].name} was` : `${r.skipped.length} people were`} nudged in the last week, so we left them.`
        : '';
      onDone(r.sent ? `Nudge sent to ${r.sent === 1 ? (person?.name ?? '1 person') : `${r.sent} people`}.${skipped}` : `No one to nudge.${skipped}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn\u2019t work. Try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={person ? `Nudge ${person.name}` : 'Nudge staff'}>
      <p className="muted">They get a notification on their phone asking them to record a lesson. Nobody gets more than one a week.</p>
      {person ? null : (
        <Choice
          label="Who"
          stacked
          value={who}
          onChange={(v) => setWho(v as 'quiet' | 'all')}
          options={[
            { value: 'quiet', label: `Everyone who hasn\u2019t recorded in two weeks (${quiet.length})` },
            { value: 'all', label: `Everyone (${members.length})` },
          ]}
        />
      )}
      <Field
        id="nudge-note"
        label="Add a note (optional)"
        value={note}
        maxLength={140}
        placeholder="e.g. Try it in one lesson this week"
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="nudge-preview" aria-label="What they see">
        <strong>Time for a lesson?</strong>
        <span>{body}</span>
      </div>
      {recent.length ? (
        <p className="faint">
          {recent.length === 1 ? `${recent[0].name} was` : `${recent.length} of them were`} nudged in the last week and will be left alone.
        </p>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      <div className="dialog-actions">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={send} loading={nudge.isPending} disabled={targets.length === recent.length}>
          Send nudge
        </Button>
      </div>
    </Dialog>
  );
}
